import React, { useEffect, useState } from "react";
import { EMA, SMA, RSI } from "technicalindicators";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const API_KEY = process.env.REACT_APP_API_KEY;
const SYMBOL = "BINANCE:BTCUSDT";
const MAX_HISTORY = 150;
const COOLDOWN = 30 * 1000; // 30 giây
const DELTA_PERCENT = 0.00001;

function App() {
  const [price, setPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [signal, setSignal] = useState("⏳ Đang chờ dữ liệu...");
  const [orders, setOrders] = useState([]);
  const [lastSignal, setLastSignal] = useState(null);
  const [lastOrderTime, setLastOrderTime] = useState(0);

  // Hàm tính SMA, EMA
  const computeIndicators = (data) => {
    const closes = data.map((d) => d.price);
    const sma7 = SMA.calculate({ period: 7, values: closes });
    const sma25 = SMA.calculate({ period: 25, values: closes });
    const sma99 = SMA.calculate({ period: 99, values: closes });
    const ema12 = EMA.calculate({ period: 12, values: closes });
    const ema26 = EMA.calculate({ period: 26, values: closes });

    const offset = (arr) => data.length - arr.length;

    return data.map((d, i) => ({
      ...d,
      sma7: sma7[i - offset(sma7)] ?? null,
      sma25: sma25[i - offset(sma25)] ?? null,
      sma99: sma99[i - offset(sma99)] ?? null,
      ema12: ema12[i - offset(ema12)] ?? null,
      ema26: ema26[i - offset(ema26)] ?? null,
    }));
  };

  // Kết nối WebSocket
  useEffect(() => {
    const socket = new WebSocket(`wss://ws.finnhub.io?token=${API_KEY}`);
    socket.onopen = () => {
      socket.send(JSON.stringify({ type: "subscribe", symbol: SYMBOL }));
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "trade") {
        msg.data.forEach((trade) => {
          const newPrice = trade.p;
          const time = new Date().toLocaleTimeString();
          setPrice(newPrice);
          setPriceHistory((prev) => {
            const updated = [...prev, { time, price: newPrice }];
            const trimmed = updated.slice(-MAX_HISTORY);
            return computeIndicators(trimmed);
          });
        });
      }
    };

    return () => socket.close();
  }, []);

  // Phân tích tín hiệu
  useEffect(() => {
    if (priceHistory.length < 30 || !price) return;

    const closes = priceHistory.map((p) => p.price);
    const ema12Arr = EMA.calculate({ period: 12, values: closes });
    const ema26Arr = EMA.calculate({ period: 26, values: closes });
    const rsiArr = RSI.calculate({ period: 14, values: closes });

    const latestEMA12 = ema12Arr.at(-1);
    const latestEMA26 = ema26Arr.at(-1);
    const latestRSI = rsiArr.at(-1);
    const latest = priceHistory.at(-1);

    if (!latestEMA12 || !latestEMA26 || !latestRSI) return;

    const delta = latestEMA12 - latestEMA26;
    const deltaUSD = price * DELTA_PERCENT;
    const now = Date.now();
    const inCooldown = now - lastOrderTime < COOLDOWN;

    const crossedUp = delta > deltaUSD && latestRSI > 55;
    const crossedDown = -delta > deltaUSD && latestRSI < 45;

    // LOG INFO
    console.log(`[INFO] Price: ${price?.toFixed(2)} | EMA12: ${latestEMA12.toFixed(2)}, EMA26: ${latestEMA26.toFixed(2)}, Δ: ${delta.toFixed(2)}, deltaUSD: ${deltaUSD.toFixed(2)}, RSI: ${latestRSI.toFixed(2)}, cooldown: ${inCooldown}`);

    if (inCooldown) {
      console.log("[SKIP] Đang trong thời gian cooldown.");
      return;
    }

    if (crossedUp && lastSignal !== "BUY") {
      setSignal("🟢 BUY");
      setLastSignal("BUY");
      setLastOrderTime(now);
      setOrders((prev) => [
        {
          time: latest.time,
          price: latest.price,
          action: "🟢 BUY",
          reason: `EMA12 (${latestEMA12.toFixed(2)}) > EMA26 (${latestEMA26.toFixed(2)}), RSI (${latestRSI.toFixed(2)}) > 55, Δ > $${deltaUSD.toFixed(2)}`,
        },
        ...prev,
      ]);
    } else if (crossedDown && lastSignal !== "SELL") {
      setSignal("🔴 SELL");
      setLastSignal("SELL");
      setLastOrderTime(now);
      setOrders((prev) => [
        {
          time: latest.time,
          price: latest.price,
          action: "🔴 SELL",
          reason: `EMA12 (${latestEMA12.toFixed(2)}) < EMA26 (${latestEMA26.toFixed(2)}), RSI (${latestRSI.toFixed(2)}) < 45, Δ > $${deltaUSD.toFixed(2)}`,
        },
        ...prev,
      ]);
    } else {
      let reasons = [];

      if (Math.abs(delta) <= deltaUSD) {
        reasons.push(`|ΔEMA| = ${delta.toFixed(2)} quá nhỏ (ngưỡng $${deltaUSD.toFixed(2)})`);
      }

      if (latestRSI >= 45 && latestRSI <= 55) {
        reasons.push(`RSI = ${latestRSI.toFixed(2)} nằm trong vùng trung tính (45–55)`);
      }

      if (inCooldown) {
        reasons.push(`Đang trong cooldown ${COOLDOWN / 1000}s`);
      }

      const reasonText = reasons.join("; ");
      console.log(`[NO SIGNAL] ${reasonText}`);
      setSignal(`⏳ Chờ tín hiệu... (${reasonText})`);
    }
  }, [priceHistory, price]);

  return (
    <div style={{ fontFamily: "sans-serif", padding: "2rem" }}>
      <h1>📈 BTC Trading Bot</h1>
      <h2>Giá hiện tại: ${price?.toFixed(2) || "Đang tải..."}</h2>
      <h2>Tín hiệu hiện tại: {signal}</h2>

      <LineChart
        width={1200}
        height={500}
        data={priceHistory}
        margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
      >
        <XAxis dataKey="time" minTickGap={40} />
        <YAxis domain={["auto", "auto"]} />
        <Tooltip />
        <Legend />
        <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
        <Line type="monotone" dataKey="price" stroke="#000" dot={false} name="Price" />
        <Line type="monotone" dataKey="sma7" stroke="#FFD700" dot={false} name="SMA(7)" />
        <Line type="monotone" dataKey="sma25" stroke="#FF69B4" dot={false} name="SMA(25)" />
        <Line type="monotone" dataKey="sma99" stroke="#800080" dot={false} name="SMA(99)" />
        <Line type="monotone" dataKey="ema12" stroke="#00BFFF" dot={false} name="EMA(12)" strokeDasharray="3 2" />
        <Line type="monotone" dataKey="ema26" stroke="#FFA500" dot={false} name="EMA(26)" strokeDasharray="4 4" />
      </LineChart>

      <h3>🧾 Lịch sử lệnh:</h3>
      <ul>
        {orders.map((order, idx) => (
          <li key={idx}>
            [{order.time}] {order.action} tại ${order.price.toFixed(2)}<br />
            <small>📌 {order.reason}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default App;
